import { GoogleMap, useJsApiLoader, Marker, InfoWindow, DirectionsService, DirectionsRenderer } from '@react-google-maps/api';
import React, { useState, useMemo } from 'react';
import { Box, Typography, CircularProgress, Paper, Chip } from '@mui/material';
import { API_KEY } from 'src/utils/pnd/PndConstants';
import mapStyle from 'src/containers/PnD/RiderLiveMap/mapStyle';
import RouteIcon from '@mui/icons-material/Route';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const OrderPathMap = ({ pickup, dropoff, orderNo }) => {
    const [activeMarker, setActiveMarker] = useState(null);
    const [response, setResponse] = useState(null);
    const { isLoaded } = useJsApiLoader({ id: 'google-map-script', googleMapsApiKey: API_KEY });

    const containerStyle = { width: '100%', height: '100%', position: 'relative' };

    const pickupPos = useMemo(() => {
        const lat = parseFloat(pickup?.latitude);
        const lng = parseFloat(pickup?.longitude);
        return isFinite(lat) && isFinite(lng) ? { lat, lng } : { lat: null, lng: null };
    }, [pickup]);

    const dropoffPos = useMemo(() => {
        const lat = parseFloat(dropoff?.latitude);
        const lng = parseFloat(dropoff?.longitude);
        return isFinite(lat) && isFinite(lng) ? { lat, lng } : { lat: null, lng: null };
    }, [dropoff]);

    const center = useMemo(() => {
        if (pickupPos.lat !== null && dropoffPos.lat !== null) {
            return {
                lat: (pickupPos.lat + dropoffPos.lat) / 2,
                lng: (pickupPos.lng + dropoffPos.lng) / 2
            };
        }
        return pickupPos.lat !== null ? pickupPos : dropoffPos.lat !== null ? dropoffPos : { lat: 25.276987, lng: 55.296249 };
    }, [pickupPos, dropoffPos]);

    const routeInfo = useMemo(() => {
        if (response && response.routes && response.routes[0] && response.routes[0].legs[0]) {
            const leg = response.routes[0].legs[0];
            return {
                distance: leg.distance.text,
                duration: leg.duration.text
            };
        }
        return null;
    }, [response]);

    const directionsCallback = (res) => {
        if (res !== null && res.status === 'OK') {
            setResponse(res);
        }
    };

    if (!isLoaded) return (
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
        </Box>
    );

    return (
        <Box sx={containerStyle}>
            {routeInfo && (
                <Paper elevation={4} sx={{
                    position: 'absolute',
                    top: 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 10,
                    p: 1.5,
                    borderRadius: 3,
                    display: 'flex',
                    gap: 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(4px)',
                    border: '1px solid #e2e8f0'
                }}>
                    <Chip
                        icon={<RouteIcon sx={{ fontSize: '18px !important' }} />}
                        label={routeInfo.distance}
                        size="small"
                        sx={{ fontWeight: 600, bgcolor: '#f0f9ff', color: '#0369a1' }}
                    />
                    <Chip
                        icon={<AccessTimeIcon sx={{ fontSize: '18px !important' }} />}
                        label={routeInfo.duration}
                        size="small"
                        sx={{ fontWeight: 600, bgcolor: '#f0fdf4', color: '#166534' }}
                    />
                </Paper>
            )}

            <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={center}
                zoom={12}
                options={{ styles: mapStyle }}
            >
                {pickupPos.lat && dropoffPos.lat && !response && (
                    <DirectionsService
                        options={{
                            destination: dropoffPos,
                            origin: pickupPos,
                            travelMode: 'DRIVING',
                        }}
                        callback={directionsCallback}
                    />
                )}

                {response !== null && (
                    <DirectionsRenderer
                        options={{
                            directions: response,
                            polylineOptions: {
                                strokeColor: '#ef4444',
                                strokeWeight: 4,
                                strokeOpacity: 0.8
                            }
                        }}
                    />
                )}

                {pickupPos.lat && (
                    <Marker
                        position={pickupPos}
                        onClick={() => setActiveMarker('pickup')}
                        label="P"
                        icon="https://maps.google.com/mapfiles/ms/icons/green-dot.png"
                    >
                        {activeMarker === 'pickup' && (
                            <InfoWindow onCloseClick={() => setActiveMarker(null)}>
                                <Typography variant="body2"><b>Pickup Point</b><br />{pickup.address}</Typography>
                            </InfoWindow>
                        )}
                    </Marker>
                )}

                {dropoffPos.lat && (
                    <Marker
                        position={dropoffPos}
                        onClick={() => setActiveMarker('dropoff')}
                        label="D"
                        icon="https://maps.google.com/mapfiles/ms/icons/red-dot.png"
                    >
                        {activeMarker === 'dropoff' && (
                            <InfoWindow onCloseClick={() => setActiveMarker(null)}>
                                <Typography variant="body2"><b>Delivery Point</b><br />{dropoff.address}</Typography>
                            </InfoWindow>
                        )}
                    </Marker>
                )}
            </GoogleMap>
        </Box>
    );
};

export default OrderPathMap;
